import {
    BrokerInfo,
    FireLeadRequestBodyV2,
    FireLeadRequestBodyV2LeadType,
    FireRequestedLeadData,
    FireWtcRequestBodyV2,
    LeadInfo,
    MainGuarantee,
    Metadata,
    ProductInfo,
    TariffV2Basic,
    TariffV2Option,
    WtcReason,
} from "@baloise-cfa/tsclient/fire";
import { getCityName } from "@lg-cfa/utils";

import { UserType } from "@enums";
import { Insurance, Tariff, UserDataInterface } from "@types";

export const generateTariffObject = (
    usertype: string,
    tariffs?: TariffV2Basic,
) => {
    const insurances: Array<Insurance> = [];
    const lowercaseUsertype = usertype.toLowerCase();

    if (tariffs && tariffs.mainGuarantees) {
        tariffs.mainGuarantees.forEach((tar) => {
            if (tar.name === "Combined") {
                insurances.unshift({
                    name: `insurancechoice.choice.combined.name.${lowercaseUsertype}`,
                    key: "combined",
                    description: "insurancechoice.choice.combined.description",
                    discountPercentage: tar.discount,
                    calculatedPrice: tar.price,
                    calculatedValue: tariffs.contentValue,
                    badge: "insurancechoice.choice.mostchosen",
                    wantToKnowMore: [
                        `insurancechoice.choice.combined.explanation1.${lowercaseUsertype}`,
                        `insurancechoice.choice.combined.explanation2.${lowercaseUsertype}`,
                    ],
                });
            } else {
                const tariffName = tar.name?.toLowerCase();
                const tariffValueKey =
                    `${tariffName}Value` as keyof TariffV2Basic;

                insurances.push({
                    name: `insurancechoice.choice.${tariffName}.name.${lowercaseUsertype}`,
                    key: tariffName ?? "",
                    description: `insurancechoice.choice.${tariffName}.description.${lowercaseUsertype}`,
                    discountPercentage: tar.discount,
                    calculatedPrice: tar.price,
                    calculatedValue: tariffs[tariffValueKey] as number,
                    wantToKnowMore: [
                        `insurancechoice.choice.${tariffName}.explanation.${lowercaseUsertype}`,
                    ],
                    options: tar.options,
                });
            }
        });
    }

    const tariffObject: Tariff = {
        propertyValue: undefined,
        contentValue: undefined,
        insurances: insurances,
        reimbursedValue: tariffs?.reimbursedValue,
    };

    return tariffObject;
};

const generateLeadInfo = (userData: UserDataInterface): LeadInfo => {
    return {
        firstName: userData.yourInformationFirstName,
        lastName: userData.yourInformationLastName,
        email: userData.yourInformationEmail,
        telephoneNumber: userData.yourInformationPhone,
        extraInfo: userData.yourInformationQuestion,
        consentGiven: userData.yourInformationConsent,
    };
};

const generateLeadData = (
    userData: UserDataInterface,
    optionTariff: TariffV2Option[],
): FireRequestedLeadData => {
    return {
        buildingType: userData.housingType,
        address: {
            city: getCityName(userData.propertyCity),
            postalCode: Number(userData.propertyPostalCode),
            street: userData.propertyStreet?.name,
            streetId: userData.propertyStreet?.id,
            houseNumber: userData.propertyNumber,
            bus: userData.propertyLetterBox,
        },
        rooms: {
            garages: userData.garages,
            others: userData.others,
            attics: userData.attics,
            outbuildings: userData.extras,
            basements: userData.cellars,
        },
        mainGuarantee: makeGuaranteeObject(userData.insurance),
        options: makeOptionsArray(
            userData.isTheftChecked,
            userData.securityDoorState,
            userData.chosenTheftOption,
            userData.isLegalChecked,
            optionTariff,
            userData.isPackageChecked,
        ),
        risk: Number(userData.propertyRisk),
    };
};

const generateBrokerInfo = (userData: UserDataInterface): BrokerInfo => {
    return {
        brokerId: userData.brokerData?.brokerId,
    };
};

const generateMetaData = (language: string): Metadata => {
    return {
        language: language === "nl-BE" ? "NLD" : "FRA",
    };
};

const generateProductInfo = (userType?: `${UserType}`): ProductInfo => {
    return {
        productType: "Fire",
        productSubType: userType,
    };
};

export const generateFireLeadObject = (
    userData: UserDataInterface,
    optionTariff: TariffV2Option[],
    userType: `${UserType}` | undefined,
    weTakeCareActivated: boolean,
    language: string,
): FireLeadRequestBodyV2 => {
    return {
        leadInfo: generateLeadInfo(userData),
        leadData: generateLeadData(userData, optionTariff),
        leadType: weTakeCareActivated
            ? FireLeadRequestBodyV2LeadType.WtcMin
            : FireLeadRequestBodyV2LeadType.QuoteLead,
        brokerInfo: generateBrokerInfo(userData),
        metaData: generateMetaData(language),
        productInfo: generateProductInfo(userType),
    };
};

export const generateWTCFireLeadObject = (
    userData: UserDataInterface,
    optionTariff: TariffV2Option[],
    userType: `${UserType}` | undefined,
    weTakeCareActivated: boolean,
    wtcReason: WtcReason,
    language: string,
): FireWtcRequestBodyV2 => {
    return {
        leadRequestBody: {
            brokerInfo: generateBrokerInfo(userData),
            leadData: generateLeadData(userData, optionTariff),
            leadInfo: generateLeadInfo(userData),
            leadType: weTakeCareActivated
                ? FireLeadRequestBodyV2LeadType.WtcMin
                : FireLeadRequestBodyV2LeadType.QuoteLead,
            metaData: generateMetaData(language),
            productInfo: generateProductInfo(userType),
        },
        wtcReason: wtcReason,
    };
};

const makeGuaranteeObject = (
    insurance?: Insurance,
): MainGuarantee | undefined => {
    if (!insurance) return undefined;
    return {
        name:
            insurance?.key?.charAt(0)?.toUpperCase() + insurance?.key?.slice(1),
        price: insurance?.calculatedPrice,
        discount: insurance?.discountPercentage,
    };
};

function makeOptionsArray(
    theftChecked: boolean,
    securityDoor: string,
    chosenTheft: string,
    legalChecked: boolean,
    optionTariff: TariffV2Option[],
    packageChecked: boolean,
): TariffV2Option[] {
    const theftOption = theftChecked
        ? optionTariff.find(
              (tariff) =>
                  tariff.theft &&
                  tariff.name === chosenTheft &&
                  (securityDoor === "door"
                      ? tariff.secureDoor
                      : !tariff.secureDoor),
          )
        : undefined;

    const legalOption = legalChecked
        ? optionTariff.find((option) => option.name === "legalProtection")
        : undefined;

    const packageOption = packageChecked
        ? optionTariff.find((option) => option.name === "surroundPackage")
        : undefined;

    const result = [theftOption, legalOption, packageOption].filter(
        (option): option is TariffV2Option => option !== undefined,
    );

    return result;
}
